import { PayloadAction } from '@reduxjs/toolkit';
import { EntityTypes } from 'constants/entities';
import { update } from 'core/actions/admin/data';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { selectAction, selectActionDAO } from 'core/selectors/product/action/action';
import { selectScene } from 'core/selectors/product/scene/scene';
import { createRequest } from 'core/services/admin';
import { uploadRequest } from 'core/services/asset';
import { IUploadFile } from 'core/slices/upload';
import { all, call, fork, put, select, takeLatest, take, debounce } from 'redux-saga/effects';
import { RootState } from 'types';
import { IAction, IActionBasic, IActionDAO } from 'types/domain/action';
import {
  IScene,
  ISceneConfig,
  ISceneConfigDAO,
  ISceneDAO,
  IScenePreview,
  SceneConfigFit,
  SceneProjection,
  SceneType,
} from 'types/domain/scene';
import { ICopySettings, ISceneEditorState, sceneEditorActions } from '.';
import { childActionSaga } from './childSaga';
import { ICameraState, updateCameraState } from 'core/slices/player';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';
import { selectSceneConfig } from 'core/selectors/product/scene/sceneConfig';
import { selectSceneEditor } from './selectSceneEditor';
import { IEditState, actionEditorActions } from 'app/components/ActionEdit/slice';
import { ICopy } from 'core/actions/admin/diagram';
import { selectPortrait } from 'core/selectors/product/portrait';

function* previewCapture(action: PayloadAction<IScenePreview>) {
  const offlineId = yield select(selectOfflineId);
  const folderId = yield select((state: RootState) => state.state[EntityTypes.FOLDER]);
  let wait = true;

  while (wait) {
    const createResponse: PayloadAction<any> = yield take(createRequest.fulfilled.type);
    if (createResponse.payload.offlineId === offlineId) {
      wait = false;
      yield put(
        yield call(uploadRequest, {
          ref: action.payload.url,
          id: createResponse.payload.id,
          folderId: createResponse.payload.folderId || folderId,
          signedUrl: createResponse.payload.signedUrl,
          contentType: createResponse.payload.contentType,
        } as IUploadFile),
      );
    }
  }
}

function* actionEditSaga(action: PayloadAction<IEditState>) {
  // const actionDAO: IActionDAO = yield select(selectActionDAO);
  // const sceneConfig: ISceneConfig = yield select(selectSceneConfig);
}

function* pasteSceneSaga(action: PayloadAction<number>) {
  const copy: ICopySettings = yield select((state: RootState) => state.sceneEditor?.copy);
  const origin: IAction = yield select(
    (state: RootState) => state.entity.present[EntityTypes.ACTION][state.sceneEditor.copy.actionId],
  );
  const target: IAction = yield select((state: RootState) => state.entity.present[EntityTypes.ACTION][action.payload]);
  yield put(yield call(sceneEditorActions.copySceneSettings, null));
  if (copy.positionId) {
    const offlineId = yield select(selectOfflineId);
    const position = yield select((state: RootState) => state.entity.present[EntityTypes.POSITION][copy.positionId]);
    const portrait = yield select(selectPortrait);
    yield put(
      yield call(update, {
        type: EntityTypes.POSITION,
        parentEntityType: EntityTypes.SCENE_CONFIG,
        parentEntityId: target.sceneConfigId,
        parentModuleId: target.moduleId,
        parentEntityAttribute: portrait ? 'pPositionId' : 'positionId',
        mergeData: true,
        entity: { ...position, id: offlineId, offlineId },
      }),
    );
  } else
    yield put(
      yield call(update, {
        type: EntityTypes.ACTION,
        parentEntityType: EntityTypes.ACTION,
        parentEntityId: origin.id,
        parentModuleId: origin.moduleId,
        mergeData: true,
        entity: { id: target.id, sceneId: origin.sceneId },
      }),
    );
}

function* setCameraDefault() {
  const mode: Mode = yield select(selectSessionMode);
  const sceneConfig: ISceneConfig = yield select(selectSceneConfig);
  const sceneEdior: ISceneEditorState = yield select(selectSceneEditor);
  const { portrait, camera } = yield select((state: RootState) => state.core.player.state);
  const fit = sceneConfig ? (portrait ? sceneConfig.pFit : sceneConfig.fit) : null;

  if (mode !== Mode.ADMIN) return;

  const position = { x: camera.position.x, y: camera.position.y, z: camera.position.z };
  const rotation = { x: camera.rotation.x, y: camera.rotation.y, z: camera.rotation.z };

  const scene: ISceneDAO = yield select(selectScene);
  const action: IActionBasic = yield select(selectAction);

  const offlineId = yield select(selectOfflineId);
  const sceneConfigId = action && action.sceneConfigId ? action.sceneConfigId : scene ? scene.sceneConfigId : null;

  const positionId =
    action && action.sceneConfig
      ? portrait
        ? action.sceneConfig.pPositionId
        : action.sceneConfig.positionId
      : scene
      ? portrait
        ? scene.sceneConfig.pPositionId
        : scene.sceneConfig.positionId
      : null;
  const rotationId =
    action && action.sceneConfig
      ? portrait
        ? action.sceneConfig.pRotationId
        : action.sceneConfig.rotationId
      : scene
      ? portrait
        ? scene.sceneConfig.pRotationId
        : scene.sceneConfig.rotationId
      : null;

  if (scene && (scene.type === SceneType.MODEL || scene.projection === SceneProjection['3603D']))
    yield put(
      yield call(update, {
        type: EntityTypes.ROTATION,
        parentEntityType: EntityTypes.SCENE_CONFIG,
        parentEntityId: sceneConfigId,
        parentModuleId: action?.moduleId,
        parentEntityAttribute: portrait ? 'pRotationId' : 'rotationId',
        entity: {
          id: rotationId || offlineId,
          offlineId,
          ...rotation,
        },
      }),
    );
  yield put(
    yield call(update, {
      type: EntityTypes.POSITION,
      parentEntityType: EntityTypes.SCENE_CONFIG,
      parentEntityId: sceneConfigId,
      parentModuleId: action?.moduleId,
      parentEntityAttribute: portrait ? 'pPositionId' : 'positionId',
      entity: {
        id: positionId || offlineId,
        offlineId,
        ...position,
      },
    }),
  );

  if (sceneConfig && sceneConfig.fit !== SceneConfigFit.POSITION)
    yield put(
      yield call(update, {
        type: EntityTypes.SCENE_CONFIG,
        entity: portrait
          ? { id: sceneConfig.id, pFit: SceneConfigFit.POSITION }
          : { id: sceneConfig.id, fit: SceneConfigFit.POSITION },
      }),
    );
}

function* resetSaga() {
  const sceneConfig: ISceneConfigDAO = yield select(selectSceneConfig);
  const portrait = yield select(selectPortrait);
  const parentCourseId = yield select((state: RootState) => state.state[EntityTypes.COURSE]);

  if (
    (portrait && sceneConfig.fit !== SceneConfigFit.POSITION) ||
    (!portrait && sceneConfig.pFit !== SceneConfigFit.POSITION)
  )
    yield put(
      yield call(update, {
        type: EntityTypes.POSITION,
        parentEntityType: EntityTypes.SCENE_CONFIG,
        parentEntityId: sceneConfig.id,
        parentCourseId,
        entity: {
          id: portrait ? sceneConfig.pPositionId : sceneConfig.positionId,
          x: 0,
          y: 0,
          z: 0,
        },
      }),
    );
}

function* updateCameraStateSaga(action: PayloadAction<ICameraState>) {
  if (action.payload.save) yield setCameraDefault();
}

function* watchPreviewCapture() {
  yield takeLatest(sceneEditorActions.scenePreviewCaptured, previewCapture);
}

function* watchSetCameraDefault() {
  yield takeLatest(sceneEditorActions.setCameraDefault, setCameraDefault);
}

function* watchActionEditor() {
  yield takeLatest(actionEditorActions.edit, actionEditSaga);
}

function* watchPasteScene() {
  yield takeLatest(sceneEditorActions.pasteSceneSettings, pasteSceneSaga);
}

function* watchReset() {
  yield takeLatest(sceneEditorActions.reset, resetSaga);
}

function* watchUpdateCamera() {
  // yield debounce(3000, updateCameraState, updateCameraStateSaga);
}

export function* SceneEditorSaga() {
  yield all([
    fork(watchSetCameraDefault),
    fork(watchPreviewCapture),
    fork(watchPasteScene),
    fork(watchUpdateCamera),
    fork(watchActionEditor),
    fork(watchReset),
  ]);
}
